import { Box, Flex, useColorModeValue, Text, IconButton, SlideFade } from '@chakra-ui/react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { OnboardingService } from '../services/onboardingService';
import Sidebar from './Sidebar';
import { useState, useEffect, useMemo } from 'react';
import { colors } from '../assets/styles/colors';
import { useTranslation } from 'react-i18next';
import { FaBars, FaGraduationCap } from 'react-icons/fa';

const Layout = () => {
  // Auth and navigation hooks
  const { user, loading: isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // UI and theme hooks
  const bgColor = useColorModeValue("gray.50", colors.primary[800]);
  const textColor = useColorModeValue(colors.primary[700], "gray.100");
  const sidebarBgColor = useColorModeValue("white", colors.primary[700]);
  const sidebarBorderColor = useColorModeValue("gray.200", colors.primary[600]);
  const gradientText = useColorModeValue(
    "linear(to-l, #7928CA, #FF0080)",
    "linear(to-l, #9062EA, #FF369F)"
  );
  const scrollbarThumbColor = useColorModeValue(
    "gray.300",
    colors.primary[600]
  );

  // State hooks
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showSidebar, setShowSidebar] = useState(!isMobile);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Automatically show sidebar on desktop, hide on mobile
      setShowSidebar(!mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Toggle sidebar visibility (for mobile)
  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
    // Set a data attribute to track sidebar state
    if (!showSidebar) {
      document.body.setAttribute("data-sidebar", "open");
    } else {
      document.body.removeAttribute("data-sidebar");
    }
  };

  // Check if we're currently on smart practice page for UI adjustments
  const isSmartPracticePage = useMemo(() => {
    return location.pathname === "/smart-practice";
  }, [location.pathname]);

  // Add/remove sidebar state classes
  useEffect(() => {
    // Set initial sidebar state
    if (showSidebar) {
      document.body.setAttribute("data-sidebar", "open");
    } else {
      document.body.removeAttribute("data-sidebar");
    }

    return () => {
      document.body.removeAttribute("data-sidebar");
    };
  }, [showSidebar]);

  // Check onboarding status and redirect if needed
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // Enhanced demo mode detection
      const isDemoMode =
        location.search.includes("demo=true") ||
        location.search.includes("from=onboarding") ||
        location.pathname.startsWith("/onboarding");

      if (!user || isLoading || isDemoMode) {
        setCheckingOnboarding(false);
        return;
      }

      try {
        setCheckingOnboarding(true);
        const hasCompleted = await OnboardingService.hasCompletedOnboarding(
          user.id
        );

        if (!hasCompleted) {
          // User hasn't completed onboarding, redirect them
          navigate("/onboarding", { replace: true });
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, [user, isLoading, navigate, location]);

  // Handle redirection if not logged in
  useEffect(() => {
    // Enhanced demo mode detection (consistent with onboarding check)
    const isDemoMode =
      location.search.includes("demo=true") ||
      location.search.includes("from=onboarding") ||
      location.pathname.startsWith("/onboarding");

    // Only redirect if not loading, user is not authenticated, and not in demo mode
    if (!isLoading && !user && !isDemoMode) {
      navigate("/login", { state: { from: location } });
    }
  }, [user, isLoading, navigate, location]);

  // Don't block rendering on onboarding checks when unauthenticated or in demo mode
  const isDemoMode =
    location.search.includes('demo=true') ||
    location.search.includes('from=onboarding') ||
    location.pathname.startsWith("/onboarding");

  if (isLoading || (user && !isDemoMode && checkingOnboarding)) {
    return (
      <Flex
        h="100vh"
        justify="center"
        align="center"
        bg={bgColor}
        direction="column"
      >
        <Box
          position="relative"
          height="80px"
          width="80px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            borderRadius="full"
            borderWidth="4px"
            borderColor="purple.500"
            borderLeftColor="transparent"
            animation="spin 1s linear infinite"
          />
          <Box
            as={FaGraduationCap}
            color="purple.500"
            fontSize="2xl"
            animation="pulse 2s ease-in-out infinite"
          />
        </Box>
        <Text
          mt={4}
          color={textColor}
          fontWeight="bold"
          bgGradient={gradientText}
          bgClip="text"
          fontSize="xl"
        >
          {checkingOnboarding ? t("onboarding.loading") : t("common.loading")}
        </Text>
      </Flex>
    );
  }

  // Don't render the layout if not authenticated (unless in demo mode)
  if (!user && !isDemoMode) {
    return null;
  }

  return (
    <Flex
      h="100vh"
      bg={bgColor}
      flexDirection={{ base: "column", md: "row" }}
      overflow="hidden" // Prevent double scrollbars
      position="relative"
    >
      {/* Animated background element */}
      <Box
        position="absolute"
        top="0"
        right="0"
        width="30%"
        height="30%"
        bgGradient="radial(circle at top right, purple.400, transparent 70%)"
        opacity={0.1}
        pointerEvents="none"
        zIndex={0}
      />

      {/* Sidebar - conditionally shown and adapted for mobile */}
      <SlideFade in={showSidebar} offsetX="-30px" unmountOnExit>
        <Box
          display={{ base: isMobile ? "block" : "none", md: "block" }}
          position={{ base: "fixed", md: "sticky" }}
          top="0"
          left="0"
          h="100vh"
          w={{ base: "full", md: "270px" }}
          zIndex={{ base: 1000, md: 1 }}
          bg={sidebarBgColor}
          borderRight="1px"
          borderRightColor={sidebarBorderColor}
          boxShadow={{ base: "xl", md: "md" }}
          transition="all 0.5s cubic-bezier(0.17, 0.67, 0.83, 0.67)"
          overflowY="auto"
          className={isMobile ? "no-scrollbar" : ""}
        >
          <Sidebar
            closeSidebar={isMobile ? () => setShowSidebar(false) : undefined}
            toggleSidebar={toggleSidebar}
            showSidebar={showSidebar}
          />
        </Box>
      </SlideFade>

      {/* Main content area */}
      <Box
        flex="1"
        display="flex"
        flexDirection="column"
        h="100vh"
        w={{ base: "100%", md: showSidebar ? "calc(100% - 270px)" : "100%" }}
        transition="all 0.5s cubic-bezier(0.17, 0.67, 0.83, 0.67)"
        position="relative"
        zIndex={1}
      >
        {/* Floating burger menu button - only visible when sidebar is closed and not in chat view */}
        {!showSidebar && !isSmartPracticePage && (
          <IconButton
            position="fixed"
            top="15px"
            left="15px"
            zIndex={1000}
            size="md"
            variant="solid"
            colorScheme="brand"
            aria-label={t("common.openNavigation")}
            icon={<FaBars />}
            onClick={toggleSidebar}
            boxShadow="md"
            borderRadius="full"
            bg="purple.500"
            _hover={{
              bg: "purple.600",
              transform: "rotate(180deg)",
            }}
            transition="all 0.3s ease"
          />
        )}

        <Box
          as="main"
          flex="1"
          overflowY="auto"
          p={{ base: 4, md: 6 }}
          position="relative"
          sx={{
            scrollbarWidth: "thin",
            scrollbarColor: `${colors.primary[300]} transparent`,
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: scrollbarThumbColor,
              borderRadius: "20px",
            },
          }}
        >
          <SlideFade in={true} offsetY="20px">
            <Outlet />
          </SlideFade>
        </Box>
      </Box>
    </Flex>
  );
};

export default Layout;
